import {
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  arrayUnion,
  collection,
  doc,
  getDocs,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
} from "firebase/firestore";

import {
  useLocation,
  useNavigate,
} from "react-router-dom";

import { FiSearch } from "react-icons/fi";

import { db } from "../firebase";

function EmployeeCalls() {
  const navigate = useNavigate();
  const location = useLocation();

  const [employee, setEmployee] =
    useState(null);

  const [employees, setEmployees] =
    useState([]);

  const [leads, setLeads] =
    useState([]);

  const [
    selectedLead,
    setSelectedLead,
  ] = useState(null);

  const [loading, setLoading] =
    useState(true);

  const [saving, setSaving] =
    useState(false);

  const [message, setMessage] =
    useState("");

  const [searchText, setSearchText] =
    useState("");

  const [
    connectionType,
    setConnectionType,
  ] = useState("");

  const [
    disposition,
    setDisposition,
  ] = useState("");

  const [
    discussion,
    setDiscussion,
  ] = useState("");

  const [
    callbackDate,
    setCallbackDate,
  ] = useState("");

  const [
    callbackTime,
    setCallbackTime,
  ] = useState("");

  const [
    selectedLanguage,
    setSelectedLanguage,
  ] = useState("");

  const [
    selectedRMId,
    setSelectedRMId,
  ] = useState("");

  const [
    editingLeadDetails,
    setEditingLeadDetails,
  ] = useState(false);

  const [
    leadDetailsSaving,
    setLeadDetailsSaving,
  ] = useState(false);

  const [
    leadDetailsForm,
    setLeadDetailsForm,
  ] = useState({
    customerName: "",
    phone: "",
    alternatePhone: "",
    email: "",
    source: "",
    requirement: "",
    priority: "Normal",
  });

  const todayDate =
    new Date()
      .toISOString()
      .split("T")[0];

  const connectedDispositions = [
    "Interested",
    "Not Interested",
    "Short Call",
    "My Prospect",
    "Follow-up Required",
    "Converted",
  ];

  const nonConnectedDispositions = [
    "Not Answered",
    "Busy",
    "Ringing",
    "Switched Off",
    "Out of Coverage",
    "Invalid Number",
  ];

  const languageOptions = [
    "English",
    "Hindi",
    "Marathi",
    "Gujarati",
    "Tamil",
    "Telugu",
    "Kannada",
    "Malayalam",
    "Bengali",
    "Punjabi",
    "Odia",
    "Assamese",
    "Other",
  ];

  useEffect(() => {
    const savedEmployee =
      localStorage.getItem(
        "crmEmployee"
      );

    if (!savedEmployee) {
      navigate(
        "/employee-login"
      );
      return;
    }

    let parsedEmployee;

    try {
      parsedEmployee =
        JSON.parse(
          savedEmployee
        );
    } catch (error) {
      localStorage.removeItem(
        "crmEmployee"
      );

      navigate(
        "/employee-login"
      );
      return;
    }

    if (
      !parsedEmployee?.id ||
      !parsedEmployee?.email
    ) {
      navigate(
        "/employee-login"
      );
      return;
    }

    setEmployee(
      parsedEmployee
    );

    const loadData =
      async () => {
        try {
          setLoading(true);
          setMessage("");

          const employeeEmail =
            parsedEmployee.email
              .trim()
              .toLowerCase();

          const leadsQuery =
            query(
              collection(
                db,
                "leads"
              ),
              where(
                "assignedEmployeeEmail",
                "==",
                employeeEmail
              )
            );

          const [
            leadsSnapshot,
            employeesSnapshot,
          ] = await Promise.all([
            getDocs(
              leadsQuery
            ),

            getDocs(
              collection(
                db,
                "employees"
              )
            ),
          ]);

          const leadList =
            leadsSnapshot.docs.map(
              (
                leadDocument
              ) => ({
                id:
                  leadDocument.id,

                ...leadDocument.data(),
              })
            );

          const employeeList =
            employeesSnapshot.docs.map(
              (
                employeeDocument
              ) => ({
                id:
                  employeeDocument.id,

                ...employeeDocument.data(),
              })
            );

          setLeads(
            leadList
          );

          setEmployees(
            employeeList
          );

          const queryParameters =
            new URLSearchParams(
              location.search
            );

          const requestedLeadId =
            queryParameters.get(
              "lead"
            );

          if (
            requestedLeadId
          ) {
            const requestedLead =
              leadList.find(
                (lead) =>
                  lead.id ===
                  requestedLeadId
              );

            if (
              requestedLead
            ) {
              setSelectedLead(
                requestedLead
              );
            }
          }
        } catch (error) {
          console.error(
            "Employee calls loading error:",
            error
          );

          setMessage(
            "Unable to load your assigned calls."
          );
        } finally {
          setLoading(false);
        }
      };

    loadData();
  }, [
    navigate,
    location.search,
  ]);

  const getTimeValue = (
    value
  ) => {
    if (!value) {
      return 0;
    }

    try {
      if (
        typeof value.toDate ===
        "function"
      ) {
        return value
          .toDate()
          .getTime();
      }

      const date =
        new Date(value);

      return Number.isNaN(
        date.getTime()
      )
        ? 0
        : date.getTime();
    } catch (error) {
      return 0;
    }
  };

  const searchedLeads =
    useMemo(() => {
      const value =
        searchText
          .trim()
          .toLowerCase();

      let list =
        [...leads];

      if (value) {
        const cleanSearch =
          value.replace(
            /\s+/g,
            ""
          );

        list =
          list.filter(
            (lead) => {
              const name =
                (
                  lead.customerName ||
                  lead.name ||
                  ""
                ).toLowerCase();

              const phone =
                String(
                  lead.phone ||
                  ""
                )
                  .replace(
                    /\s+/g,
                    ""
                  )
                  .toLowerCase();

              const alternatePhone =
                String(
                  lead.alternatePhone ||
                  lead.alternateNumber ||
                  ""
                )
                  .replace(
                    /\s+/g,
                    ""
                  )
                  .toLowerCase();

              const email =
                (
                  lead.email ||
                  ""
                ).toLowerCase();

              return (
                name.includes(
                  value
                ) ||
                phone.includes(
                  cleanSearch
                ) ||
                alternatePhone.includes(
                  cleanSearch
                ) ||
                email.includes(
                  value
                )
              );
            }
          );
      }

      return list.sort(
        (a, b) => {
          const aWorked =
            getTimeValue(
              a.lastContactedAt
            );

          const bWorked =
            getTimeValue(
              b.lastContactedAt
            );

          /*
            FRESH LEADS FIRST
          */

          if (
            !aWorked &&
            bWorked
          ) {
            return -1;
          }

          if (
            aWorked &&
            !bWorked
          ) {
            return 1;
          }

          /*
            BOTH FRESH:
            newest fresh lead first
          */

          if (
            !aWorked &&
            !bWorked
          ) {
            return (
              getTimeValue(
                b.createdAt
              ) -
              getTimeValue(
                a.createdAt
              )
            );
          }

          /*
            BOTH WORKED:

            older worked lead above,
            newest updated lead at bottom
          */

          return (
            aWorked -
            bWorked
          );
        }
      );
    }, [
      leads,
      searchText,
    ]);

  const availableRMs =
    useMemo(() => {
      if (
        !selectedLanguage
      ) {
        return [];
      }

      return employees.filter(
        (
          employeeItem
        ) => {
          if (
            employeeItem.status ===
            "Inactive"
          ) {
            return false;
          }

          if (
            employeeItem.id ===
            employee?.id
          ) {
            return false;
          }

          const languages =
            Array.isArray(
              employeeItem.languages
            )
              ? employeeItem.languages
              : [];

          return languages.includes(
            selectedLanguage
          );
        }
      );
    }, [
      employees,
      employee,
      selectedLanguage,
    ]);

  const resetUpdateForm = () => {
    setConnectionType("");
    setDisposition("");
    setDiscussion("");
    setCallbackDate("");
    setCallbackTime("");
    setSelectedLanguage("");
    setSelectedRMId("");
  };

  const handleOpenLead = (
    lead
  ) => {
    setSelectedLead(
      lead
    );

    resetUpdateForm();

    setEditingLeadDetails(
      false
    );

    setMessage("");

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const handleBackToList = () => {
    setSelectedLead(null);

    resetUpdateForm();

    setEditingLeadDetails(
      false
    );

    setMessage("");

    navigate(
      "/employee-calls",
      {
        replace: true,
      }
    );
  };

  const updateEmployeeActivity =
    async () => {
      if (
        !employee?.id
      ) {
        return;
      }

      const activityTime =
        new Date()
          .toISOString();

      try {
        await updateDoc(
          doc(
            db,
            "employees",
            employee.id
          ),
          {
            lastActivityAt:
              activityTime,

            attendanceUpdatedAt:
              serverTimestamp(),
          }
        );
      } catch (error) {
        console.error(
          "Activity update error:",
          error
        );
      }
    };

  const createProspect =
    async (
      lead,
      historyEntry
    ) => {
      const prospectId =
        lead.prospectId ||
        lead.id;

      const prospectReference =
        doc(
          db,
          "prospects",
          prospectId
        );

      await setDoc(
        prospectReference,
        {
          leadId:
            lead.id,

          customerName:
            lead.customerName ||
            lead.name ||
            "Customer",

          phone:
            lead.phone ||
            "",

          email:
            lead.email ||
            "",

          source:
            lead.source ||
            "",

          requirement:
            lead.requirement ||
            "",

          priority:
            lead.priority ||
            "Normal",

          assignedEmployeeId:
            employee.id,

          assignedEmployeeName:
            employee.name ||
            "Employee",

          assignedEmployeeEmail:
            employee.email
              .trim()
              .toLowerCase(),

          temperature:
            "Warm",

          prospectTemperature:
            "Warm",

          status:
            "Active Prospect",

          stage:
            "New Prospect",

          prospectStage:
            "New Prospect",

          latestRemark:
            discussion.trim(),

          nextFollowUpDate:
            "",

          nextFollowUpTime:
            "",

          remarksHistory: [
            historyEntry,
          ],

          createdAt:
            serverTimestamp(),

          updatedAt:
            serverTimestamp(),
        },
        {
          merge: true,
        }
      );

      return prospectId;
    };

  const handleSaveUpdate =
    async () => {
      if (
        !selectedLead ||
        !employee
      ) {
        return;
      }

      if (
        !connectionType
      ) {
        setMessage(
          "Select Connected, Non Connected or Language Barrier."
        );

        return;
      }

      if (
        connectionType ===
        "Connected"
      ) {
        if (
          !disposition
        ) {
          setMessage(
            "Select a disposition."
          );

          return;
        }

        if (
          !discussion.trim()
        ) {
          setMessage(
            "Enter complete discussion in short."
          );

          return;
        }

        if (
          disposition ===
            "Follow-up Required" &&
          (
            !callbackDate ||
            !callbackTime
          )
        ) {
          setMessage(
            "Select callback date and time."
          );

          return;
        }
      }

      if (
        connectionType ===
          "Non Connected" &&
        !disposition
      ) {
        setMessage(
          "Select a non-connected disposition."
        );

        return;
      }

      if (
        connectionType ===
        "Language Barrier"
      ) {
        if (
          !selectedLanguage
        ) {
          setMessage(
            "Select customer language."
          );

          return;
        }

        if (
          !selectedRMId
        ) {
          setMessage(
            "Select an available RM."
          );

          return;
        }
      }

      try {
        setSaving(true);
        setMessage("");

        const activityTime =
          new Date()
            .toISOString();

        const selectedRM =
          employees.find(
            (
              employeeItem
            ) =>
              employeeItem.id ===
              selectedRMId
          );

        let finalStatus =
          disposition;

        if (
          connectionType ===
          "Language Barrier"
        ) {
          finalStatus =
            "Language Transfer";
        }

        if (
          disposition ===
          "My Prospect"
        ) {
          finalStatus =
            "Prospect";
        }

        const historyEntry = {
          employeeId:
            employee.id,

          employeeName:
            employee.name ||
            "Employee",

          employeeEmail:
            employee.email ||
            "",

          connectionType,

          disposition:
            finalStatus,

          discussion:
            connectionType ===
            "Connected"
              ? discussion.trim()
              : "",

          remark:
            connectionType ===
            "Connected"
              ? discussion.trim()
              : finalStatus,

          language:
            connectionType ===
            "Language Barrier"
              ? selectedLanguage
              : "",

          transferredToEmployeeId:
            connectionType ===
            "Language Barrier"
              ? selectedRM?.id ||
                ""
              : "",

          transferredToEmployeeName:
            connectionType ===
            "Language Barrier"
              ? selectedRM?.name ||
                ""
              : "",

          callbackDate:
            disposition ===
            "Follow-up Required"
              ? callbackDate
              : "",

          callbackTime:
            disposition ===
            "Follow-up Required"
              ? callbackTime
              : "",

          createdAt:
            activityTime,
        };

        let prospectId =
          selectedLead.prospectId ||
          "";

        if (
          disposition ===
          "My Prospect"
        ) {
          prospectId =
            await createProspect(
              selectedLead,
              historyEntry
            );
        }

        const updateData = {
          callConnectionType:
            connectionType,

          status:
            finalStatus,

          lastDisposition:
            finalStatus,

          lastRemark:
            connectionType ===
            "Connected"
              ? discussion.trim()
              : finalStatus,

          /*
            THIS FIELD CONTROLS SORTING
          */
          lastContactedAt:
            serverTimestamp(),

          updatedAt:
            serverTimestamp(),

          remarksHistory:
            arrayUnion(
              historyEntry
            ),
        };

        if (
          disposition ===
          "Follow-up Required"
        ) {
          updateData.callbackStatus =
            "Pending";

          updateData.callbackDate =
            callbackDate;

          updateData.callbackTime =
            callbackTime;
        } else {
          updateData.callbackStatus =
            "";

          updateData.callbackDate =
            "";

          updateData.callbackTime =
            "";
        }

        if (
          disposition ===
          "My Prospect"
        ) {
          updateData.isProspect =
            true;

          updateData.prospectId =
            prospectId;

          updateData.prospectTemperature =
            "Warm";
        }

        if (
          connectionType ===
          "Language Barrier"
        ) {
          updateData.preferredLanguage =
            selectedLanguage;

          updateData.assignedEmployeeId =
            selectedRM.id;

          updateData.assignedEmployeeName =
            selectedRM.name ||
            "";

          updateData.assignedEmployeeEmail =
            selectedRM.email
              .trim()
              .toLowerCase();

          updateData.transferredByEmployeeId =
            employee.id;

          updateData.transferredByEmployeeName =
            employee.name ||
            "";

          updateData.transferredAt =
            serverTimestamp();
        }

        await updateDoc(
          doc(
            db,
            "leads",
            selectedLead.id
          ),
          updateData
        );

        await updateEmployeeActivity();

        if (
          connectionType ===
          "Language Barrier"
        ) {
          setLeads(
            (
              currentLeads
            ) =>
              currentLeads.filter(
                (lead) =>
                  lead.id !==
                  selectedLead.id
              )
          );

          setSelectedLead(
            null
          );

          resetUpdateForm();

          setMessage(
            `Lead transferred to ${selectedRM.name}.`
          );

          navigate(
            "/employee-calls"
          );

          return;
        }

        const updatedLead = {
          ...selectedLead,

          ...updateData,

          remarksHistory: [
            ...(Array.isArray(
              selectedLead.remarksHistory
            )
              ? selectedLead.remarksHistory
              : []),

            historyEntry,
          ],

          /*
            IMPORTANT:
            local timestamp is assigned here
            immediately so sorting happens
            WITHOUT a refresh.
          */
          lastContactedAt:
            activityTime,
        };

        setLeads(
          (
            currentLeads
          ) =>
            currentLeads.map(
              (lead) =>
                lead.id ===
                selectedLead.id
                  ? updatedLead
                  : lead
            )
        );

        setSelectedLead(
          updatedLead
        );

        resetUpdateForm();

        setMessage(
          disposition ===
          "My Prospect"
            ? "Lead successfully added to My Prospect."
            : "Call update saved successfully."
        );
      } catch (error) {
        console.error(
          "Save call update error:",
          error
        );

        setMessage(
          `Unable to save: ${
            error.message ||
            "Firebase permission error"
          }`
        );
      } finally {
        setSaving(false);
      }
    };

  const startLeadDetailsEdit = () => {
    if (
      !selectedLead
    ) {
      return;
    }

    setLeadDetailsForm({
      customerName:
        selectedLead.customerName ||
        selectedLead.name ||
        "",

      phone:
        selectedLead.phone ||
        "",

      alternatePhone:
        selectedLead.alternatePhone ||
        selectedLead.alternateNumber ||
        "",

      email:
        selectedLead.email ||
        "",

      source:
        selectedLead.source ||
        "",

      requirement:
        selectedLead.requirement ||
        "",

      priority:
        selectedLead.priority ||
        "Normal",
    });

    setEditingLeadDetails(
      true
    );

    setMessage("");
  };

  const handleLeadDetailsInputChange = (
    event
  ) => {
    const {
      name,
      value,
    } = event.target;

    setLeadDetailsForm(
      (current) => ({
        ...current,
        [name]: value,
      })
    );
  };

  const handleSaveLeadDetails =
    async () => {
      if (
        !selectedLead ||
        !employee
      ) {
        return;
      }

      const newValues = {
        customerName:
          leadDetailsForm.customerName.trim(),

        phone:
          leadDetailsForm.phone.trim(),

        alternatePhone:
          leadDetailsForm.alternatePhone.trim(),

        email:
          leadDetailsForm.email.trim(),

        source:
          leadDetailsForm.source.trim(),

        requirement:
          leadDetailsForm.requirement.trim(),

        priority:
          leadDetailsForm.priority ||
          "Normal",
      };

      if (
        !newValues.customerName
      ) {
        setMessage(
          "Customer name is required."
        );

        return;
      }

      if (
        !newValues.phone
      ) {
        setMessage(
          "Primary phone number is required."
        );

        return;
      }

      const fields = [
        {
          key:
            "customerName",

          label:
            "Customer Name",

          before:
            selectedLead.customerName ||
            selectedLead.name ||
            "",
        },

        {
          key:
            "phone",

          label:
            "Primary Number",

          before:
            selectedLead.phone ||
            "",
        },

        {
          key:
            "alternatePhone",

          label:
            "Alternate Number",

          before:
            selectedLead.alternatePhone ||
            selectedLead.alternateNumber ||
            "",
        },

        {
          key:
            "email",

          label:
            "Email",

          before:
            selectedLead.email ||
            "",
        },

        {
          key:
            "source",

          label:
            "Lead Source",

          before:
            selectedLead.source ||
            "",
        },

        {
          key:
            "requirement",

          label:
            "Customer Requirement",

          before:
            selectedLead.requirement ||
            "",
        },

        {
          key:
            "priority",

          label:
            "Priority",

          before:
            selectedLead.priority ||
            "Normal",
        },
      ];

      const changes =
        fields
          .filter(
            (field) =>
              String(
                field.before ??
                ""
              ) !==
              String(
                newValues[
                  field.key
                ] ??
                ""
              )
          )
          .map(
            (field) => ({
              field:
                field.key,

              label:
                field.label,

              before:
                String(
                  field.before ??
                  ""
                ),

              after:
                String(
                  newValues[
                    field.key
                  ] ??
                  ""
                ),
            })
          );

      if (
        changes.length ===
        0
      ) {
        setMessage(
          "No lead detail changes to save."
        );

        setEditingLeadDetails(
          false
        );

        return;
      }

      try {
        setLeadDetailsSaving(
          true
        );

        setMessage("");

        const changedAt =
          new Date()
            .toISOString();

        const editEntry = {
          changedById:
            employee.id ||
            "",

          changedByName:
            employee.name ||
            "Employee",

          changedByEmail:
            employee.email ||
            "",

          changedByRole:
            "Employee",

          changedAt,

          changes,
        };

        const updateData = {
          customerName:
            newValues.customerName,

          name:
            newValues.customerName,

          phone:
            newValues.phone,

          alternatePhone:
            newValues.alternatePhone,

          email:
            newValues.email,

          source:
            newValues.source,

          requirement:
            newValues.requirement,

          priority:
            newValues.priority,

          leadEditHistory:
            arrayUnion(
              editEntry
            ),

          updatedAt:
            serverTimestamp(),
        };

        await updateDoc(
          doc(
            db,
            "leads",
            selectedLead.id
          ),
          updateData
        );

        const oldEditHistory =
          Array.isArray(
            selectedLead.leadEditHistory
          )
            ? selectedLead.leadEditHistory
            : [];

        const updatedLead = {
          ...selectedLead,

          ...newValues,

          name:
            newValues.customerName,

          leadEditHistory: [
            ...oldEditHistory,
            editEntry,
          ],
        };

        setSelectedLead(
          updatedLead
        );

        setLeads(
          (
            currentLeads
          ) =>
            currentLeads.map(
              (lead) =>
                lead.id ===
                selectedLead.id
                  ? updatedLead
                  : lead
            )
        );

        setEditingLeadDetails(
          false
        );

        setMessage(
          "Lead details updated successfully."
        );

        await updateEmployeeActivity();
      } catch (error) {
        console.error(
          "Employee lead edit error:",
          error
        );

        setMessage(
          `Unable to update lead: ${
            error.message ||
            "Firebase permission error"
          }`
        );
      } finally {
        setLeadDetailsSaving(
          false
        );
      }
    };

  const formatHistoryDate = (
    historyItem
  ) => {
    if (
      !historyItem?.createdAt
    ) {
      return "Imported / date not available";
    }

    try {
      return new Date(
        historyItem.createdAt
      ).toLocaleString(
        "en-IN",
        {
          day: "2-digit",
          month: "short",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        }
      );
    } catch (error) {
      return "Date unavailable";
    }
  };

  const getHistoryRemark = (
    historyItem
  ) => {
    return (
      historyItem.discussion ||
      historyItem.remark ||
      historyItem.disposition ||
      "No remark"
    );
  };

  const getHistoryStatus = (
    historyItem
  ) => {
    if (
      historyItem.connectionType ===
      "Imported"
    ) {
      return "Imported Previous Remark";
    }

    if (
      historyItem.connectionType ===
      "Language Barrier"
    ) {
      return "Language Barrier";
    }

    return (
      historyItem.disposition ||
      historyItem.status ||
      historyItem.connectionType ||
      "Previous Call"
    );
  };

  if (
    selectedLead
  ) {
    const history =
      Array.isArray(
        selectedLead.remarksHistory
      )
        ? [
            ...selectedLead.remarksHistory,
          ].reverse()
        : [];

    const leadEditHistory =
      Array.isArray(
        selectedLead.leadEditHistory
      )
        ? [
            ...selectedLead.leadEditHistory,
          ].reverse()
        : [];

    return (
      <div className="employee-calls-page">

        <header className="mobile-crm-header">

          <div>

            <h1>
              Trading Wealth CRM
            </h1>

            <p>
              Update Call
            </p>

          </div>

          <button
            type="button"
            className="mobile-back-button"
            onClick={() =>
              navigate(
                "/employee-dashboard"
              )
            }
          >
            Dashboard
          </button>

        </header>

        <main className="employee-calls-main">

          <button
            type="button"
            className="back-to-leads-button"
            onClick={
              handleBackToList
            }
          >
            ← Back to My Calls
          </button>

          {message && (

            <div className="attendance-message">
              {message}
            </div>

          )}

          <section className="customer-call-card">

            <div>

              <p className="small-label">
                CUSTOMER
              </p>

              <h2>
                {selectedLead.customerName ||
                  selectedLead.name ||
                  "Customer"}
              </h2>

              <p>
                {selectedLead.phone ||
                  "-"}
              </p>

              {(
                selectedLead.alternatePhone ||
                selectedLead.alternateNumber
              ) && (

                <p>
                  Alternate:{" "}
                  {selectedLead.alternatePhone ||
                    selectedLead.alternateNumber}
                </p>

              )}

            </div>

            <a
              className="call-customer-button"
              href={`tel:${
                selectedLead.phone ||
                ""
              }`}
              onClick={
                updateEmployeeActivity
              }
            >
              Call Customer
            </a>

          </section>

          <section className="employee-update-card">

            <div
              style={{
                display: "flex",
                justifyContent:
                  "space-between",
                alignItems:
                  "center",
                gap: "10px",
                flexWrap:
                  "wrap",
              }}
            >

              <div>

                <h3>
                  Customer Details
                </h3>

                <p>
                  Employees can update basic customer details.
                </p>

              </div>

              {!editingLeadDetails && (

                <button
                  type="button"
                  className="secondary-button"
                  onClick={
                    startLeadDetailsEdit
                  }
                >
                  Edit Lead Details
                </button>

              )}

            </div>

            {editingLeadDetails ? (

              <>

                <div className="employee-form-grid">

                  <div className="form-group">

                    <label>
                      Customer Name *
                    </label>

                    <input
                      type="text"
                      name="customerName"
                      value={
                        leadDetailsForm.customerName
                      }
                      onChange={
                        handleLeadDetailsInputChange
                      }
                    />

                  </div>

                  <div className="form-group">

                    <label>
                      Primary Number *
                    </label>

                    <input
                      type="tel"
                      name="phone"
                      value={
                        leadDetailsForm.phone
                      }
                      onChange={
                        handleLeadDetailsInputChange
                      }
                    />

                  </div>

                  <div className="form-group">

                    <label>
                      Alternate Number
                    </label>

                    <input
                      type="tel"
                      name="alternatePhone"
                      value={
                        leadDetailsForm.alternatePhone
                      }
                      onChange={
                        handleLeadDetailsInputChange
                      }
                    />

                  </div>

                  <div className="form-group">

                    <label>
                      Email
                    </label>

                    <input
                      type="email"
                      name="email"
                      value={
                        leadDetailsForm.email
                      }
                      onChange={
                        handleLeadDetailsInputChange
                      }
                    />

                  </div>

                  <div className="form-group">

                    <label>
                      Lead Source
                    </label>

                    <input
                      type="text"
                      name="source"
                      value={
                        leadDetailsForm.source
                      }
                      onChange={
                        handleLeadDetailsInputChange
                      }
                    />

                  </div>

                  <div className="form-group">

                    <label>
                      Priority
                    </label>

                    <select
                      name="priority"
                      value={
                        leadDetailsForm.priority
                      }
                      onChange={
                        handleLeadDetailsInputChange
                      }
                    >

                      <option value="Low">
                        Low
                      </option>

                      <option value="Normal">
                        Normal
                      </option>

                      <option value="High">
                        High
                      </option>

                      <option value="Urgent">
                        Urgent
                      </option>

                    </select>

                  </div>

                  <div className="form-group">

                    <label>
                      Requirement
                    </label>

                    <textarea
                      className="crm-textarea"
                      name="requirement"
                      value={
                        leadDetailsForm.requirement
                      }
                      onChange={
                        handleLeadDetailsInputChange
                      }
                    />

                  </div>

                </div>

                <div className="employee-form-actions">

                  <button
                    type="button"
                    className="save-call-update-button"
                    disabled={
                      leadDetailsSaving
                    }
                    onClick={
                      handleSaveLeadDetails
                    }
                  >
                    {leadDetailsSaving
                      ? "Saving..."
                      : "Save Lead Details"}
                  </button>

                  <button
                    type="button"
                    className="secondary-button"
                    disabled={
                      leadDetailsSaving
                    }
                    onClick={() => {
                      setEditingLeadDetails(
                        false
                      );

                      setMessage("");
                    }}
                  >
                    Cancel
                  </button>

                </div>

              </>

            ) : (

              <div className="lead-information-grid">

                <div>

                  <span>
                    Customer Name
                  </span>

                  <strong>
                    {selectedLead.customerName ||
                      selectedLead.name ||
                      "-"}
                  </strong>

                </div>

                <div>

                  <span>
                    Primary Number
                  </span>

                  <strong>
                    {selectedLead.phone ||
                      "-"}
                  </strong>

                </div>

                <div>

                  <span>
                    Alternate Number
                  </span>

                  <strong>
                    {selectedLead.alternatePhone ||
                      selectedLead.alternateNumber ||
                      "-"}
                  </strong>

                </div>

                <div>

                  <span>
                    Email
                  </span>

                  <strong>
                    {selectedLead.email ||
                      "-"}
                  </strong>

                </div>

                <div>

                  <span>
                    Source
                  </span>

                  <strong>
                    {selectedLead.source ||
                      "-"}
                  </strong>

                </div>

                <div>

                  <span>
                    Priority
                  </span>

                  <strong>
                    {selectedLead.priority ||
                      "Normal"}
                  </strong>

                </div>

                <div>

                  <span>
                    Requirement
                  </span>

                  <strong>
                    {selectedLead.requirement ||
                      "-"}
                  </strong>

                </div>

              </div>

            )}

          </section>

          <section className="employee-update-card">

            <h3>
              Update Call Result
            </h3>

            <div className="call-status-grid">

              {[
                "Connected",
                "Non Connected",
                "Language Barrier",
              ].map(
                (option) => (

                  <button
                    key={
                      option
                    }
                    type="button"
                    className={
                      connectionType ===
                      option
                        ? "call-status-button selected"
                        : "call-status-button"
                    }
                    onClick={() => {
                      setConnectionType(
                        option
                      );

                      setDisposition(
                        ""
                      );

                      setDiscussion(
                        ""
                      );

                      setCallbackDate(
                        ""
                      );

                      setCallbackTime(
                        ""
                      );

                      setSelectedLanguage(
                        ""
                      );

                      setSelectedRMId(
                        ""
                      );
                    }}
                  >
                    {option}
                  </button>

                )
              )}

            </div>

            {connectionType ===
              "Connected" && (

              <>

                <div className="form-group">

                  <label>
                    Disposition *
                  </label>

                  <select
                    value={
                      disposition
                    }
                    onChange={(
                      event
                    ) =>
                      setDisposition(
                        event.target.value
                      )
                    }
                  >

                    <option value="">
                      Select disposition
                    </option>

                    {connectedDispositions.map(
                      (
                        option
                      ) => (

                        <option
                          key={
                            option
                          }
                          value={
                            option
                          }
                        >
                          {option}
                        </option>

                      )
                    )}

                  </select>

                </div>

                {disposition ===
                  "Follow-up Required" && (

                  <div className="callback-fields-grid">

                    <div className="form-group">

                      <label>
                        Callback Date *
                      </label>

                      <input
                        type="date"
                        min={
                          todayDate
                        }
                        value={
                          callbackDate
                        }
                        onChange={(
                          event
                        ) =>
                          setCallbackDate(
                            event.target.value
                          )
                        }
                      />

                    </div>

                    <div className="form-group">

                      <label>
                        Callback Time *
                      </label>

                      <input
                        type="time"
                        value={
                          callbackTime
                        }
                        onChange={(
                          event
                        ) =>
                          setCallbackTime(
                            event.target.value
                          )
                        }
                      />

                    </div>

                  </div>

                )}

                <div className="form-group">

                  <label>
                    Complete Discussion in Short *
                  </label>

                  <textarea
                    className="crm-textarea"
                    value={
                      discussion
                    }
                    onChange={(
                      event
                    ) =>
                      setDiscussion(
                        event.target.value
                      )
                    }
                  />

                </div>

              </>

            )}

            {connectionType ===
              "Non Connected" && (

              <div className="form-group">

                <label>
                  Disposition *
                </label>

                <select
                  value={
                    disposition
                  }
                  onChange={(
                    event
                  ) =>
                    setDisposition(
                      event.target.value
                    )
                  }
                >

                  <option value="">
                    Select disposition
                  </option>

                  {nonConnectedDispositions.map(
                    (
                      option
                    ) => (

                      <option
                        key={
                          option
                        }
                        value={
                          option
                        }
                      >
                        {option}
                      </option>

                    )
                  )}

                </select>

              </div>

            )}

            {connectionType ===
              "Language Barrier" && (

              <>

                <div className="form-group">

                  <label>
                    Customer Language *
                  </label>

                  <select
                    value={
                      selectedLanguage
                    }
                    onChange={(
                      event
                    ) => {
                      setSelectedLanguage(
                        event.target.value
                      );

                      setSelectedRMId(
                        ""
                      );
                    }}
                  >

                    <option value="">
                      Select language
                    </option>

                    {languageOptions.map(
                      (
                        language
                      ) => (

                        <option
                          key={
                            language
                          }
                          value={
                            language
                          }
                        >
                          {language}
                        </option>

                      )
                    )}

                  </select>

                </div>

                <div className="form-group">

                  <label>
                    Available RM *
                  </label>

                  <select
                    value={
                      selectedRMId
                    }
                    onChange={(
                      event
                    ) =>
                      setSelectedRMId(
                        event.target.value
                      )
                    }
                  >

                    <option value="">
                      Select RM
                    </option>

                    {availableRMs.map(
                      (
                        rm
                      ) => (

                        <option
                          key={
                            rm.id
                          }
                          value={
                            rm.id
                          }
                        >
                          {rm.name}
                        </option>

                      )
                    )}

                  </select>

                </div>

              </>

            )}

            {connectionType && (

              <button
                type="button"
                className="save-call-update-button"
                onClick={
                  handleSaveUpdate
                }
                disabled={
                  saving
                }
              >
                {saving
                  ? "Saving..."
                  : connectionType ===
                    "Language Barrier"
                    ? "Transfer Lead"
                    : disposition ===
                      "My Prospect"
                      ? "Add to My Prospect"
                      : "Save Call Update"}
              </button>

            )}

          </section>

          <section className="remark-history-card">

            <h3>
              Lead Detail Edit History
            </h3>

            {leadEditHistory.length ===
              0 ? (

              <p className="no-remarks-text">
                No lead detail edits recorded.
              </p>

            ) : (

              <div className="remark-timeline">

                {leadEditHistory.map(
                  (
                    editItem,
                    index
                  ) => (

                    <div
                      key={
                        `${editItem.changedAt}-${index}`
                      }
                      className="remark-history-item"
                    >

                      <div className="remark-history-top">

                        <strong>
                          {editItem.changedByName ||
                            "User"}
                        </strong>

                        <span>
                          {editItem.changedAt
                            ? new Date(
                                editItem.changedAt
                              ).toLocaleString(
                                "en-IN"
                              )
                            : "-"}
                        </span>

                      </div>

                      <p>
                        {editItem.changedByRole ||
                          ""}
                        {editItem.changedByEmail
                          ? ` • ${editItem.changedByEmail}`
                          : ""}
                      </p>

                      {Array.isArray(
                        editItem.changes
                      ) &&
                        editItem.changes
                          .filter(
                            (
                              change
                            ) =>
                              change.field !==
                              "adminNote"
                          )
                          .map(
                            (
                              change,
                              changeIndex
                            ) => (

                              <div
                                key={
                                  changeIndex
                                }
                                style={{
                                  marginTop:
                                    "10px",
                                }}
                              >

                                <strong>
                                  {change.label ||
                                    change.field}
                                </strong>

                                <p>
                                  Before:{" "}
                                  {change.before ||
                                    "-"}
                                </p>

                                <p>
                                  After:{" "}
                                  {change.after ||
                                    "-"}
                                </p>

                              </div>

                            )
                          )}

                    </div>

                  )
                )}

              </div>

            )}

          </section>

          <section className="remark-history-card">

            <h3>
              Previous Call History
            </h3>

            {history.length ===
              0 ? (

              <p className="no-remarks-text">
                No previous call history.
              </p>

            ) : (

              <div className="remark-timeline">

                {history.map(
                  (
                    historyItem,
                    index
                  ) => (

                    <div
                      key={
                        `${historyItem.createdAt}-${index}`
                      }
                      className="remark-history-item"
                    >

                      <div className="remark-history-top">

                        <strong>
                          {historyItem.employeeName ||
                            "Employee"}
                        </strong>

                        <span>
                          {formatHistoryDate(
                            historyItem
                          )}
                        </span>

                      </div>

                      <p>

                        <strong>
                          {getHistoryStatus(
                            historyItem
                          )}
                        </strong>

                      </p>

                      <p>
                        {getHistoryRemark(
                          historyItem
                        )}
                      </p>

                      {historyItem.callbackDate && (

                        <small>
                          Callback:{" "}
                          {
                            historyItem.callbackDate
                          }{" "}
                          {
                            historyItem.callbackTime
                          }
                        </small>

                      )}

                      {historyItem.language && (

                        <small>
                          Language:{" "}
                          {
                            historyItem.language
                          }
                        </small>

                      )}

                      {historyItem.transferredToEmployeeName && (

                        <small>
                          Transferred to:{" "}
                          {
                            historyItem.transferredToEmployeeName
                          }
                        </small>

                      )}

                    </div>

                  )
                )}

              </div>

            )}

          </section>

        </main>

      </div>
    );
  }

  return (
    <div className="employee-calls-page">

      <header className="mobile-crm-header">

        <div>

          <h1>
            Trading Wealth CRM
          </h1>

          <p>
            My Assigned Calls
          </p>

        </div>

        <button
          type="button"
          className="mobile-back-button"
          onClick={() =>
            navigate(
              "/employee-dashboard"
            )
          }
        >
          Dashboard
        </button>

      </header>

      <main className="employee-calls-main">

        <section className="employee-calls-heading">

          <p className="small-label">
            ASSIGNED CUSTOMERS
          </p>

          <h2>
            {employee?.name ||
              "Employee"}
            ’s Leads
          </h2>

          <p>
            Fresh leads stay on top. Once updated, the customer moves toward the bottom automatically.
          </p>

        </section>

        <div
          style={{
            position:
              "relative",
            marginBottom:
              "18px",
          }}
        >

          <FiSearch
            size={21}
            style={{
              position:
                "absolute",
              left:
                "16px",
              top:
                "50%",
              transform:
                "translateY(-50%)",
              color:
                "#0284c7",
            }}
          />

          <input
            type="text"
            value={
              searchText
            }
            onChange={(
              event
            ) =>
              setSearchText(
                event.target.value
              )
            }
            placeholder="Search name, phone, alternate phone or email"
            style={{
              width:
                "100%",
              minHeight:
                "52px",
              padding:
                "0 16px 0 48px",
              border:
                "1px solid #bae6fd",
              borderRadius:
                "14px",
              background:
                "#ffffff",
              fontSize:
                "15px",
              outline:
                "none",
              boxSizing:
                "border-box",
            }}
          />

        </div>

        <div className="employee-filter-tabs">

          <button
            type="button"
            className="employee-filter-button active"
          >
            All
          </button>

          <button
            type="button"
            className="employee-filter-button"
            onClick={() =>
              navigate(
                "/employee-callbacks"
              )
            }
          >
            Today’s Callback
          </button>

          <button
            type="button"
            className="employee-filter-button"
            onClick={() =>
              navigate(
                "/employee-prospects"
              )
            }
          >
            My Prospect
          </button>

          <button
            type="button"
            className="employee-filter-button"
            onClick={() =>
              navigate(
                "/employee-work-queue"
              )
            }
          >
            Work to Non Work
          </button>

          <button
            type="button"
            className="employee-filter-button"
            onClick={() =>
              navigate(
                "/employee-converted"
              )
            }
          >
            Converted
          </button>

        </div>

        {loading ? (

          <div className="employee-empty-state">
            Loading assigned leads...
          </div>

        ) : searchedLeads.length ===
          0 ? (

          <div className="employee-empty-state">
            No customer found.
          </div>

        ) : (

          <div className="mobile-lead-list">

            {searchedLeads.map(
              (
                lead
              ) => (

                <article
                  className="mobile-lead-card"
                  key={
                    lead.id
                  }
                >

                  <div className="mobile-lead-card-top">

                    <div>

                      <h3>
                        {lead.customerName ||
                          lead.name ||
                          "Customer"}
                      </h3>

                      <p>
                        {lead.phone ||
                          "-"}
                      </p>

                    </div>

                    <span className="mobile-status pending">
                      {lead.status ||
                        "Pending"}
                    </span>

                  </div>

                  {lead.lastRemark && (

                    <p className="latest-remark-preview">
                      Latest:{" "}
                      {lead.lastRemark}
                    </p>

                  )}

                  <div className="mobile-lead-actions">

                    <a
                      className="small-call-button"
                      href={`tel:${
                        lead.phone ||
                        ""
                      }`}
                    >
                      Call
                    </a>

                    <button
                      type="button"
                      className="open-lead-button"
                      onClick={() =>
                        handleOpenLead(
                          lead
                        )
                      }
                    >
                      Open & Update
                    </button>

                  </div>

                </article>

              )
            )}

          </div>

        )}

      </main>

    </div>
  );
}

export default EmployeeCalls;